// The "Signals out" table of the Signal builder: one row per output (= one
// Signal) with where it reads from, how the value is adjusted, what the sample
// produces and whether it works. Pure - the editor only renders it.
import type { MappingRule, MappingSpec } from './mappingEngine';
import type { PreviewRecordResult } from './mappingPreview';
import { getOutputs } from './mappingSpecEdit';

export type RowStatus = 'ok' | 'warn' | 'error' | 'info';

export interface SignalRow {
  index: number; // the output's real index in the mapping
  name: string;
  from: string;
  adjust: string;
  preview: string;
  status: RowStatus;
  statusText: string;
  reads: string; // the list the output reads (metrics documents), '' = the mapping's
}

const SYMBOL: Record<string, string> = { '/': '÷', '*': '×', '+': '+', '-': '−' };

const fmtNum = (n: number): string => (Number.isInteger(n) ? n.toLocaleString('en-US') : String(n));

// "÷ 1,000,000", "success → 1", "bytes → GB" style one-liner for how a rule changes its value.
export function describeAdjust(rule: MappingRule | undefined): string {
  if (!rule) return '—';
  const parts: string[] = [];
  if (typeof rule.unitScale === 'number' && rule.unitScale !== 1 && rule.unitScale !== 0) {
    const inv = 1 / rule.unitScale;
    parts.push(rule.unitScale < 1 && Number.isInteger(Math.round(inv * 1e6) / 1e6) ? `÷ ${fmtNum(Math.round(inv * 1e6) / 1e6)}` : `× ${fmtNum(rule.unitScale)}`);
  }
  if (Array.isArray(rule.ops)) for (const o of rule.ops) parts.push(`${SYMBOL[o.op] ?? o.op} ${o.from ? o.from : o.value ?? '?'}`);
  if (rule.map && typeof rule.map === 'object' && !Array.isArray(rule.map)) {
    const e = Object.entries(rule.map);
    parts.push(e.length <= 2 ? e.map(([k, v]) => `${k} → ${String(v)}`).join(', ') : `map (${e.length} values)`);
  }
  if (rule.default !== undefined && !parts.length) parts.push(`default ${JSON.stringify(rule.default)}`);
  return parts.length ? parts.join(', ') : '—';
}

function nameOf(rules: MappingRule[]): string {
  const n = rules.find((r) => r.to === 'name');
  if (n?.const !== undefined && n.const !== '') return String(n.const);
  if (n?.from) return `by ${n.from}`;
  const ref = rules.find((r) => r.to === 'signalRef');
  if (ref) return ref.const !== undefined ? `signal ${String(ref.const)}` : ref.from ? `signal by ${ref.from}` : 'signal ref';
  return '(unnamed)';
}

function sourceOf(rules: MappingRule[]): string {
  const v = rules.find((r) => r.to === 'value');
  if (!v) return '—';
  if (v.from) return v.from;
  if (v.const !== undefined) return `= ${String(v.const)}`;
  return '—';
}

const ERROR_TEXT: Record<string, string> = {
  'dropped-required': 'Error: required',
  'no-name': 'Error: no name',
  'no-rules': 'Error: no rules',
  'skipped-non-finite': 'Error: not a number',
  'filtered-allowlist': 'Filtered out',
};

export function formatPreview(v: string): string {
  const n = Number(v);
  if (v.trim() === '' || !Number.isFinite(n)) return v;
  return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

export function buildSignalRows(spec: MappingSpec, results: PreviewRecordResult[] | null, ownPath: (o: number) => string = () => ''): SignalRow[] {
  const outputs = getOutputs(spec);
  const first: Record<number, string> = {};
  const emitted: Record<number, number> = {};
  const failed: Record<number, { status: string; n: number }> = {};
  for (const r of results ?? []) {
    for (const o of r.outputs) {
      if (o.status === 'emitted' && o.signal) {
        if (first[o.outputIndex] === undefined) first[o.outputIndex] = o.signal.value;
        emitted[o.outputIndex] = (emitted[o.outputIndex] ?? 0) + 1;
      } else {
        const f = failed[o.outputIndex] ?? { status: o.status, n: 0 };
        f.n++;
        failed[o.outputIndex] = f;
      }
    }
  }
  return outputs.map((rules, index) => {
    const valueRule = rules.find((r) => r.to === 'value');
    const base = { index, name: nameOf(rules), from: sourceOf(rules), adjust: describeAdjust(valueRule), reads: ownPath(index) };
    const ok = emitted[index] ?? 0;
    const bad = failed[index];
    if (ok > 0) {
      const preview = formatPreview(first[index] ?? '');
      return bad && bad.status !== 'unresolved-signal-ref' ? { ...base, preview, status: 'warn', statusText: `OK · ${bad.n} rejected` } : { ...base, preview, status: 'ok', statusText: 'OK' };
    }
    if (bad?.status === 'unresolved-signal-ref') return { ...base, preview: '—', status: 'info', statusText: 'Signal ref' };
    if (bad) return { ...base, preview: 'empty', status: 'error', statusText: ERROR_TEXT[bad.status] ?? `Error: ${bad.status}` };
    return { ...base, preview: '—', status: 'info', statusText: results ? 'No sample' : 'No sample yet' };
  });
}

export function matchesRow(row: SignalRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || `${row.name} ${row.from} ${row.adjust} ${row.reads} ${row.statusText}`.toLowerCase().includes(q);
}

// "none" or the steps in use, for the "Prepare records" chip.
export function transformSummary(spec: MappingSpec): string {
  const t = spec.transform;
  if (!t) return 'none';
  const parts: string[] = [];
  if (t.groupBy?.length || t.aggregate?.length) parts.push('group');
  if (t.sortBy?.length) parts.push('sort');
  if (typeof t.limit === 'number') parts.push(`limit ${t.limit}`);
  if (t.select?.length) parts.push('select');
  return parts.length ? parts.join(', ') : 'none';
}
